import React from 'react'
import PropTypes from 'prop-types'
import { clone, cloneDeep } from 'lodash'
import { parsePluralVSSingular } from '@pcln/home-components'

const parseValidDisplayLabel = (travelerSelectionContent) => (
  { roomCount, adultCount, youthCount, childCount, infantCount },
  displaySet
) => {
  const areMultipleTypesInDisplaySet = displaySet.size > 1
  const isSingleTypeInDisplaySet = displaySet.size === 1

  const { TRAVELER_TYPES } = travelerSelectionContent

  const hasInfantType = displaySet.has(TRAVELER_TYPES.LAP_INFANTS)

  const hasRoomType = displaySet.has(TRAVELER_TYPES.ROOMS)

  const parseMultipleTypesWithInfantType = (hasRoomType = false, roomCount) =>
    `${adultCount +
      youthCount +
      childCount} Passengers, ${parsePluralVSSingular(
      infantCount,
      'Lap Infant'
    )}` + `${hasRoomType ? `, ${parsePluralVSSingular(roomCount, 'Room')}` : ''}`

  const parseMultipleTypes = () => {
    if (hasInfantType) {
      return parseMultipleTypesWithInfantType(hasRoomType, roomCount)
    }

    if (hasRoomType) {
      let label = `${parsePluralVSSingular(
        adultCount,
        'Adult'
      )}, `
      if (youthCount > 0) {
        label += `${parsePluralVSSingular(youthCount, 'Youth')}, `
      }
      if (childCount > 0) {
        label += `${parsePluralVSSingular(
          childCount,
          'Child'
        )}, `
      }
      label += `${parsePluralVSSingular(roomCount, 'Room')}`
      return label
    }
    return `${adultCount + youthCount + childCount} Passengers`
  }

  const parseSingleType = isSingleTypeInDisplaySet => {
    const extractedData = [...displaySet]
    const displayType = extractedData[0]
    let labelCount = 0
    let label = ''

    const { TRAVELER_TYPES } = travelerSelectionContent

    if (displayType === TRAVELER_TYPES.YOUTHS) {
      labelCount = youthCount
      label = 'Youth'
    } else if (displayType === TRAVELER_TYPES.CHILDREN) {
      labelCount = childCount
      label = 'Child'
    }

    return isSingleTypeInDisplaySet
      ? `${parsePluralVSSingular(
        adultCount,
        'Adult'
      )}, ${parsePluralVSSingular(labelCount, label)}`
      : `${parsePluralVSSingular(adultCount, 'Adult')}`
  }

  return areMultipleTypesInDisplaySet
    ? parseMultipleTypes()
    : `
      ${hasInfantType
    ? hasRoomType
      ? `${parsePluralVSSingular(
        adultCount,
        'Adult'
      )}, ${parsePluralVSSingular(roomCount, 'Room')}`
      : `${parsePluralVSSingular(
        adultCount,
        'Adult'
      )}, ${parsePluralVSSingular(infantCount, 'Lap Infant')}`
    : hasRoomType
      ? `${parsePluralVSSingular(
        adultCount,
        'Adult'
      )}, ${parsePluralVSSingular(roomCount, 'Room')}`
      : `${parseSingleType(isSingleTypeInDisplaySet)}`}
    `
}

const parseDisplay = (travelerSelectionContent, isUsingRoomControl) => travelers => {
  const {
    roomCount,
    adultCount,
    youthCount,
    childCount,
    infantCount
  } = travelers

  const validDisplaySet = new Set()
  const { TRAVELER_TYPES } = travelerSelectionContent

  // no need to parse adult count as minimum count is always at least 1
  if (roomCount > 0 && isUsingRoomControl) {
    validDisplaySet.add(TRAVELER_TYPES.ROOMS)
  }
  if (youthCount > 0) validDisplaySet.add(TRAVELER_TYPES.YOUTHS)
  if (childCount > 0) validDisplaySet.add(TRAVELER_TYPES.CHILDREN)
  if (infantCount > 0) validDisplaySet.add(TRAVELER_TYPES.LAP_INFANTS)

  return parseValidDisplayLabel(travelerSelectionContent)(
    {
      roomCount,
      adultCount,
      youthCount,
      childCount,
      infantCount
    },
    validDisplaySet
  )
}

class DataProvider extends React.Component {
  static defaultProps = {
    MIN_ROOM_COUNT: 1,
    MAX_ROOM_COUNT: 4,
    MAX_TRAVELER_COUNT: 8,
    isUsingRoomControl: true,
    initChildAges: {}
  }

  static propTypes = {
    MIN_ROOM_COUNT: PropTypes.number,
    MAX_ROOM_COUNT: PropTypes.number,
    MAX_TRAVELER_COUNT: PropTypes.number,
    travelerSelectionContent: PropTypes.object.isRequired,
    minAdultCount: PropTypes.number,
    initNumYouths: PropTypes.number,
    initNumRooms: PropTypes.number,
    initNumAdults: PropTypes.number,
    initNumChildren: PropTypes.number,
    initNumInfants: PropTypes.number,
    initChildAges: PropTypes.object,
    isUsingRoomControl: PropTypes.bool,
    onChange: PropTypes.func,
    render: PropTypes.func
  }

  constructor (props) {
    super(props)
    const { BUTTON_TYPE, TRAVELER_TYPES } = props.travelerSelectionContent
    const initialTravelers = {
      roomCount: Number(props.initNumRooms) || 0,
      adultCount: Number(props.initNumAdults) || 0,
      youthCount: Number(props.initNumYouths) || 0,
      childCount: Number(props.initNumChildren) || 0,
      infantCount: Number(props.initNumInfants) || 0,
      childAges: props.initChildAges,
      displayLabel: ''
    }

    const disabledIcons = new Set()
    if (initialTravelers.adultCount <= props.minAdultCount) {
      disabledIcons.add(
        `${TRAVELER_TYPES.ADULTS}_${BUTTON_TYPE.MINUS}`
      )
    }

    this.state = {
      disabledIcons,
      isControlPanelOpen: false,
      isChildAgeOpen: false,
      hasError: false,
      errorMessage: '',
      isUsingRoomControl: false,
      travelers: {
        ...initialTravelers,
        displayLabel: parseDisplay(props.travelerSelectionContent, props.isUsingRoomControl)(initialTravelers)
      }
    }
  }

  static getDerivedStateFromProps (props, state) {
    const adultCount = Number(props.initNumAdults)
    const youthCount = Number(props.initNumYouths)
    const infantCount = Number(props.initNumInfants)
    const childCount = Number(props.initNumChildren)
    const roomCount = Number(props.initNumRooms)
    const newTravelers = {
      ...state.travelers,
      roomCount,
      adultCount,
      youthCount,
      infantCount,
      childCount,
      childAges: props.initChildAges
    }
    if (
      state.travelers.adultCount !== adultCount ||
      state.travelers.childCount !== childCount ||
      state.travelers.infantCount !== infantCount ||
      state.travelers.youthCount !== youthCount ||
      state.travelers.roomCount !== roomCount
    ) {
      return {
        ...state,
        travelers: {
          ...state.travelers,
          ...newTravelers,
          isUsingRoomControl: !props.isUsingRoomControl,
          displayLabel: parseDisplay(props.travelerSelectionContent, props.isUsingRoomControl)(newTravelers)
        }
      }
    }
    const { TRAVELER_TYPES, BUTTON_TYPE } = props.travelerSelectionContent
    const disabledIcons = state.disabledIcons
    if (state.travelers.adultCount > props.minAdultCount) {
      disabledIcons.delete(`${TRAVELER_TYPES.ADULTS}_${BUTTON_TYPE.MINUS}`)
    }
    return {
      ...state,
      disabledIcons,
      travelers: {
        ...state.travelers,
        displayLabel: parseDisplay(
          props.travelerSelectionContent,
          props.isUsingRoomControl
        )(newTravelers)
      }
    }
  }

  static childContextTypes = {
    constants: PropTypes.shape({
      TRAVELER_TYPES: PropTypes.object.isRequired,
      TRAVELER_SUBTEXTS: PropTypes.object.isRequired,
      TRAVELER_LABELS: PropTypes.object.isRequired
    }).isRequired
  }

  getChildContext () {
    const {
      TRAVELER_TYPES,
      TRAVELER_LABELS,
      TRAVELER_SUBTEXTS
    } = this.props.travelerSelectionContent

    return {
      constants: {
        TRAVELER_TYPES: TRAVELER_TYPES,
        TRAVELER_SUBTEXTS: TRAVELER_SUBTEXTS,
        TRAVELER_LABELS: TRAVELER_LABELS
      }
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (nextProps) {
    const { BUTTON_TYPE, TRAVELER_TYPES } = this.props.travelerSelectionContent
    const isMiusSignForAdultsDisable =
      this.state.travelers.adultCount <= nextProps.minAdultCount
    this.updateControlButton(
      BUTTON_TYPE.MINUS,
      isMiusSignForAdultsDisable,
      TRAVELER_TYPES.ADULTS
    )
    this.setState({
      hasError: !!nextProps.error,
      errorMessage: nextProps.error
    })

    const deepClonedTravelers = cloneDeep(this.state.travelers)
    deepClonedTravelers.displayLabel = parseDisplay(
      this.props.travelerSelectionContent,
      this.props.isUsingRoomControl
    )(this.state.travelers)

    this.setState({
      travelers: deepClonedTravelers
    })
  }

  toggleIsControlPanelOpenState = (isForceOpen = false) => {
    if (isForceOpen) {
      this.setState({ isControlPanelOpen: isForceOpen })
    } else {
      this.setState({ isControlPanelOpen: !this.state.isControlPanelOpen })
    }
  }

  // TODO: Flight search is the only 1 client now.
  //       When a new VP Search Form component uses Traveler Selection,
  //       double check .onChange method's return object data structure
  updateChildAge = (childIndex, value) => {
    let parsedAgeData = {}
    parsedAgeData[childIndex] = value

    const clonedTravelers = clone(this.state.travelers)
    clonedTravelers.childAges[childIndex] = value
    this.setState({ travelers: clonedTravelers })
    this.props.onChange({ childAges: clonedTravelers.childAges })

    this.setState({ isControlPanelOpen: true })
  }

  deleteLastChildAge = (childCount = null) => {
    if (!childCount) return

    const clonedTravelers = clone(this.state.travelers)
    const clonedChildAges = clonedTravelers.childAges
    delete clonedChildAges[`${childCount}`]

    this.props.onChange({ childAges: clonedChildAges })

    return clonedChildAges
  }
  // end of to-do

  updateControlButton = (
    buttonType = null,
    isDisabled = false,
    travelerType = null
  ) => {
    if (!buttonType || !travelerType) return

    if (isDisabled) {
      this.state.disabledIcons.add(`${travelerType}_${buttonType}`)
    } else {
      this.state.disabledIcons.delete(`${travelerType}_${buttonType}`)
    }
  }

  updatePlusSignForAdultsYouthsChildren = (isDisabled = false) => {
    const { BUTTON_TYPE, TRAVELER_TYPES } = this.props.travelerSelectionContent

    if (isDisabled) {
      this.state.disabledIcons.add(
        `${TRAVELER_TYPES.ADULTS}_${BUTTON_TYPE.PLUS}`
      )
      this.state.disabledIcons.add(
        `${TRAVELER_TYPES.YOUTHS}_${BUTTON_TYPE.PLUS}`
      )
      this.state.disabledIcons.add(
        `${TRAVELER_TYPES.CHILDREN}_${BUTTON_TYPE.PLUS}`
      )
    } else {
      this.state.disabledIcons.delete(
        `${TRAVELER_TYPES.ADULTS}_${BUTTON_TYPE.PLUS}`
      )
      this.state.disabledIcons.delete(
        `${TRAVELER_TYPES.YOUTHS}_${BUTTON_TYPE.PLUS}`
      )
      this.state.disabledIcons.delete(
        `${TRAVELER_TYPES.CHILDREN}_${BUTTON_TYPE.PLUS}`
      )
    }
  }

  adjustTravelerCount = data => {
    const { BUTTON_TYPE, TRAVELER_TYPES } = this.props.travelerSelectionContent

    const { adultCount, youthCount, childCount, infantCount } = data

    const totalExceptionInfantCount = adultCount + youthCount + childCount

    const isMiusSignForAdultsDisable = adultCount <= this.props.minAdultCount
    this.updateControlButton(
      BUTTON_TYPE.MINUS,
      isMiusSignForAdultsDisable,
      TRAVELER_TYPES.ADULTS
    )

    if (totalExceptionInfantCount >= this.props.MAX_TRAVELER_COUNT) {
      const isDisabled = true
      this.updateControlButton(
        BUTTON_TYPE.PLUS,
        isDisabled,
        TRAVELER_TYPES.CHILDREN
      )
    } else {
      const isPlusSignForChildrenDisabled = adultCount <= 0
      this.updateControlButton(
        BUTTON_TYPE.PLUS,
        isPlusSignForChildrenDisabled,
        TRAVELER_TYPES.CHILDREN
      )
    }

    const isPlusSignForInfantsDisabled = adultCount <= infantCount
    this.updateControlButton(
      BUTTON_TYPE.PLUS,
      isPlusSignForInfantsDisabled,
      TRAVELER_TYPES.LAP_INFANTS
    )

    const shouldAdultCountBeAdjusted = adultCount < this.props.minAdultCount
    const shouldChildCountBeAdjusted = adultCount <= 0
    const shouldInfantCountBeAdjusted = adultCount < infantCount

    return {
      adultCount: shouldAdultCountBeAdjusted ? adultCount - 1 : adultCount,
      youthCount,
      childCount: shouldChildCountBeAdjusted ? 0 : childCount,
      infantCount: shouldInfantCountBeAdjusted ? infantCount - 1 : infantCount
    }
  }

  updateTravelerCount = (travelerType, isIncrement) => {
    const { BUTTON_TYPE, TRAVELER_TYPES } = this.props.travelerSelectionContent
    this.setState(prevState => {
      const {
        roomCount,
        adultCount,
        youthCount,
        childCount,
        infantCount,
        childAges
      } = prevState.travelers

      const validateTravelerCount = (count, isIncrement) => {
        const newCount = isIncrement ? count + 1 : count - 1

        const isMinusSignDisabled = newCount <= 0
        this.updateControlButton(
          BUTTON_TYPE.MINUS,
          isMinusSignDisabled,
          travelerType
        )

        let totalTravelerCountExceptInfants = 1
        switch (travelerType) {
          case TRAVELER_TYPES.ADULTS:
            totalTravelerCountExceptInfants = newCount + youthCount + childCount
            break
          case TRAVELER_TYPES.YOUTHS:
            totalTravelerCountExceptInfants = adultCount + newCount + childCount
            break
          case TRAVELER_TYPES.CHILDREN:
            totalTravelerCountExceptInfants = adultCount + youthCount + newCount
            break
          default:
            totalTravelerCountExceptInfants =
              adultCount + youthCount + childCount
            break
        }

        const {
          MIN_ROOM_COUNT,
          MAX_ROOM_COUNT,
          MAX_TRAVELER_COUNT
        } = this.props

        const isRoomUpdate = travelerType === TRAVELER_TYPES.ROOMS
        let isTotalRoomCountAtMax = false
        if (isRoomUpdate) {
          const isTotalRoomCountBelowMin = newCount <= MIN_ROOM_COUNT
          this.updateControlButton(
            BUTTON_TYPE.MINUS,
            isTotalRoomCountBelowMin,
            TRAVELER_TYPES.ROOMS
          )

          isTotalRoomCountAtMax = newCount >= MAX_ROOM_COUNT
          this.updateControlButton(
            BUTTON_TYPE.PLUS,
            isTotalRoomCountAtMax,
            TRAVELER_TYPES.ROOMS
          )
        }

        const isInfantUpdate = travelerType === TRAVELER_TYPES.LAP_INFANTS
        if (isInfantUpdate) {
          const isPlusSignForInfantsDisabled = newCount >= adultCount
          this.updateControlButton(
            BUTTON_TYPE.PLUS,
            isPlusSignForInfantsDisabled,
            TRAVELER_TYPES.LAP_INFANTS
          )
        }

        const isBelowMinRoomCount = isRoomUpdate && newCount < MIN_ROOM_COUNT

        const isTotalTravelerCountAtMax =
          totalTravelerCountExceptInfants >= MAX_TRAVELER_COUNT
        this.updatePlusSignForAdultsYouthsChildren(isTotalTravelerCountAtMax)

        const isInfantCountMoreThanAdultCount =
          isInfantUpdate && newCount > adultCount
        const isBelowZero = newCount < 0

        return isBelowMinRoomCount ||
          isTotalRoomCountAtMax > MAX_ROOM_COUNT ||
          isTotalTravelerCountAtMax > MAX_TRAVELER_COUNT ||
          isInfantCountMoreThanAdultCount ||
          isBelowZero
          ? count
          : newCount
      }

      let travelers = {}

      switch (travelerType) {
        case TRAVELER_TYPES.ROOMS:
          travelers = {
            roomCount: validateTravelerCount(roomCount, isIncrement),
            adultCount,
            youthCount,
            childCount,
            infantCount,
            childAges
          }

          this.props.onChange({
            travelerType: TRAVELER_TYPES.ROOMS,
            count: travelers.roomCount
          })
          break
        case TRAVELER_TYPES.ADULTS:
          const validatedAdultCount = validateTravelerCount(
            adultCount,
            isIncrement
          )

          const adjustedDataPerAdult = this.adjustTravelerCount({
            adultCount: validatedAdultCount,
            youthCount,
            childCount,
            infantCount
          })

          travelers = {
            roomCount,
            adultCount: validatedAdultCount,
            youthCount,
            childCount: adjustedDataPerAdult.childCount,
            infantCount: adjustedDataPerAdult.infantCount,
            childAges
          }

          this.props.onChange({
            travelerType: TRAVELER_TYPES.ADULTS,
            count: travelers.adultCount
          })
          break
        case TRAVELER_TYPES.YOUTHS:
          const validatedYouthCount = validateTravelerCount(
            youthCount,
            isIncrement
          )
          const adjustedDataPerYouth = this.adjustTravelerCount({
            adultCount,
            youthCount: validatedYouthCount,
            childCount,
            infantCount
          })

          travelers = {
            roomCount,
            adultCount,
            youthCount: validatedYouthCount,
            childCount: adjustedDataPerYouth.childCount,
            infantCount: adjustedDataPerYouth.infantCount,
            childAges
          }
          this.props.onChange({
            travelerType: TRAVELER_TYPES.YOUTHS,
            count: travelers.youthCount
          })
          break
        case TRAVELER_TYPES.CHILDREN:
          const validatedChildCount = validateTravelerCount(
            childCount,
            isIncrement
          )

          const parsedChildAges = !isIncrement
            ? this.deleteLastChildAge(childCount)
            : this.state.travelers.childAges

          const adjustedDataPerChild = this.adjustTravelerCount({
            adultCount,
            youthCount,
            childCount: validatedChildCount,
            infantCount
          })

          travelers = {
            roomCount,
            adultCount,
            youthCount,
            childCount: adjustedDataPerChild.childCount,
            infantCount: adjustedDataPerChild.infantCount,
            childAges: parsedChildAges
          }

          this.props.onChange({
            travelerType: TRAVELER_TYPES.CHILDREN,
            count: travelers.childCount
          })
          break
        case TRAVELER_TYPES.LAP_INFANTS:
          travelers = {
            roomCount,
            adultCount,
            youthCount,
            childCount,
            infantCount: validateTravelerCount(infantCount, isIncrement),
            childAges
          }
          this.props.onChange({
            travelerType: TRAVELER_TYPES.LAP_INFANTS,
            count: travelers.infantCount
          })
          break
        default:
          travelers = prevState.travelers
          break
      }

      travelers.displayLabel = parseDisplay(
        this.props.travelerSelectionContent,
        this.props.isUsingRoomControl)(travelers)
      return Object.assign(cloneDeep(prevState), { travelers })
    })
  }

  render () {
    return this.props.render(
      Object.assign(
        { state: this.state },
        { props: this.props },
        { toggleIsControlPanelOpenState: this.toggleIsControlPanelOpenState },
        { updateChildAge: this.updateChildAge },
        { updateTravelerCount: this.updateTravelerCount }
      )
    )
  }
}

DataProvider.displayName = 'DataProvider'

export default DataProvider
